import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TAPE, STATS, PILLARS } from './data';

// HOME — institutional desk dashboard. Continuously animating ticker tape + KPI strip.
// Visually distinct from Mercurial: warm amber on graphite, serif headline, no terminal chrome.
export default function Home({ setPage }) {
  // Animate price last digit subtly to feel "live"
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 2400); return () => clearInterval(id); }, []);
  return (
    <div className="bg-[#14110F] text-[#F8F4ED]">
      {/* TICKER STRIP — full-width scrolling tape under header */}
      <section className="pt-16 bg-[#0D0B0A] border-y border-[#2A241F] overflow-hidden">
        <motion.div animate={{ x: ['0%', '-50%'] }} transition={{ duration: 60, repeat: Infinity, ease: 'linear' }} className="flex gap-10 py-3 whitespace-nowrap font-mono text-xs">
          {[...TAPE, ...TAPE, ...TAPE].map((t, i) => (
            <span key={i} className="flex items-center gap-3 shrink-0">
              <span className="text-[#F59E0B]">{t.sym}</span>
              <span className="text-[#F8F4ED]">{t.last}</span>
              <span className={t.state === 'up' ? 'text-[#34D399]' : 'text-[#F87171]'}>{t.ch}</span>
              <span className="text-[#F8F4ED]/40">vol {t.vol}</span>
              <span className="text-[#2A241F]">·</span>
            </span>
          ))}
        </motion.div>
      </section>

      {/* HEADLINE — left-aligned editorial */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-end">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] mb-5">— Institutional desk · {new Date().toUTCString().slice(17, 25)} UTC · live —</p>
            <h1 className="font-serif text-[58px] sm:text-[92px] lg:text-[140px] leading-[0.86] tracking-[-0.02em]">
              An <span className="italic text-[#F59E0B]">institutional</span><br />digital-asset desk.<br /><span className="text-[#F8F4ED]/50">Block · Custody · Yield.</span>
            </h1>
            <p className="font-sans text-base text-[#F8F4ED]/70 mt-7 max-w-xl leading-[1.7]">$4.8B in custody. Forty-seven LPs. Two-sided quotes on 186 assets in under 800ms. Audited annually. Insured to $400M. Authorised and regulated; client funds segregated.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => setPage('access')} data-testid="aem-home-cta" className="bg-[#F59E0B] text-[#14110F] px-7 py-3.5 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#FFB94A] transition-colors">Request desk access →</button>
              <button onClick={() => setPage('tape')} className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F8F4ED] border-b border-[#F8F4ED]/40 pb-1 hover:text-[#F59E0B] hover:border-[#F59E0B]">Open the tape →</button>
            </div>
          </div>
          {/* PRICE TILE — single live BTC card */}
          <motion.div key={tick} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} className="bg-[#1F1A16] border border-[#2A241F] p-7 shadow-[0_0_60px_rgba(245,158,11,0.08)]">
            <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F59E0B]">BTC / USD · spot</p>
            <p className="font-serif text-6xl mt-3 tabular-nums">${(94418.2 + (tick % 17) * 1.4).toFixed(2)}</p>
            <p className="font-mono text-sm text-[#34D399] mt-1">+0.42% / 24h</p>
            <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-[#2A241F] font-mono text-[10px] tracking-[0.22em] uppercase text-[#F8F4ED]/55">
              <div><p>24h vol</p><p className="text-[#F8F4ED] mt-1 normal-case tracking-normal">$2.41B</p></div>
              <div><p>OI</p><p className="text-[#F8F4ED] mt-1 normal-case tracking-normal">$31.4B</p></div>
              <div><p>Funding</p><p className="text-[#34D399] mt-1 normal-case tracking-normal">+0.014%</p></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* KPI STRIP */}
      <section className="border-y border-[#2A241F] bg-[#1A1612]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid md:grid-cols-4 divide-x divide-[#2A241F]">
          {STATS.map(s => (
            <div key={s.k} className="px-6 py-7">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F59E0B]">{s.k}</p>
              <p className="font-serif text-4xl lg:text-5xl text-[#F8F4ED] mt-2">{s.v}</p>
              <p className="font-mono text-xs text-[#F8F4ED]/55 mt-2" dangerouslySetInnerHTML={{ __html: s.n }} />
            </div>
          ))}
        </div>
      </section>

      {/* THREE PILLARS — large numbered chapters */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-16 space-y-10">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F59E0B] mb-2">— Three desks under one roof</p>
        {PILLARS.map((p, i) => (
          <motion.article key={p.code} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid lg:grid-cols-[180px_1fr] gap-8 items-start border-b border-[#2A241F] pb-10">
            <p className="font-serif text-[120px] lg:text-[180px] leading-[0.78] text-[#F59E0B]/80 tabular-nums">{p.code}</p>
            <div>
              <h3 className="font-serif italic text-4xl lg:text-6xl text-[#F8F4ED] leading-[1]">{p.t}</h3>
              <p className="font-sans text-base text-[#F8F4ED]/70 mt-4 leading-[1.7] max-w-2xl">{p.d}</p>
            </div>
          </motion.article>
        ))}
      </section>
    </div>
  );
}
